import React, { useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import './PlaidLinkComponent.css';
import { createLinkToken, exchangePublicToken } from '../api/plaid';

const PlaidLinkComponent = ({ linkToken: providedLinkToken, onSuccess, onClose }) => {
  const [linkToken, setLinkToken] = useState(providedLinkToken || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (providedLinkToken) { setLinkToken(providedLinkToken); return; }
    const getLinkToken = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await createLinkToken();
        if (data?.linkToken) { setLinkToken(data.linkToken); } else { setError('Failed to get link token'); }
      } catch { setError('Failed to initialize Plaid Link. Please try again.'); }
      finally { setLoading(false); }
    };
    getLinkToken();
  }, [providedLinkToken]);

  const onPlaidSuccess = async (publicToken, metadata) => {
    setLoading(true);
    setError(null);
    try {
      const result = await exchangePublicToken(publicToken);
      if (onSuccess) onSuccess(result);
    } catch { setError('Failed to connect account. Please try again.'); }
    finally { setLoading(false); }
  };

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err, metadata) => {
      if (err) {
        console.error('Plaid Link error:', err);
        setError('Connection failed. Please try again.');
      }
      if (onClose) {
        onClose();
      }
    }
  };

  const { open, ready } = usePlaidLink(config);

  // Auto-open Plaid Link when ready (similar to SnapTrade modal behavior)
  useEffect(() => {
    if (ready && linkToken && !loading) {
      open();
    }
  }, [ready, linkToken, loading, open]);

  // Only show loading if we don't have a token yet (and it wasn't provided)
  if (!providedLinkToken && loading && !linkToken) {
    return (
      <div className="plaid-loading">
        <div className="loading-spinner"></div>
        <p>Initializing secure connection...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="plaid-error">
        <p>{error}</p>
        <button 
          className="retry-btn" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!linkToken) {
    return (
      <div className="plaid-error">
        <p>Unable to initialize Plaid Link</p>
      </div>
    );
  }

  // If we have a token but Plaid Link isn't ready yet, don't show anything
  // The useEffect will auto-open when ready
  if (!ready) {
    return null; // Don't show anything, just wait for Plaid Link to be ready
  }

  // Once ready, the useEffect will auto-open the Plaid Link modal
  // Return null since the modal will open automatically
  return null;
};

export default PlaidLinkComponent;
